<script setup lang="ts">
import type { IntegrationConfig } from "@kaleidoscope-solutions/engine-wasm"
import { IntegrationDescriptor, IntegrationTarget } from "@kaleidoscope-solutions/engine-wasm"
import { DialogSize, KButton, KCheckBox, KConfirmButton, KDialog, KTextInput } from "@kaleidoscope-solutions/ks-design-system"
import { CheckRingIcon, InfoIcon } from "@kaleidoscope-solutions/ks-icons/vue/fill"
import { ArrowDownIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { storeToRefs } from "pinia"
import { computed, onMounted, ref, watch } from "vue"
import Loading from "@/components/Loading.vue"
import { Integration, IntegrationRunStatus, useIntegrationStore } from "@/stores"
import { integrationTargetLabel } from "@/utils/integrationLabels"

const integrationStore = useIntegrationStore()
const { importTargets, integrationStatus, currentIntegrationConfig } = storeToRefs(integrationStore)

const isRunning = computed(() => integrationStatus.value === IntegrationRunStatus.Running)
const hasShopifyConfiguration = computed(() => integrationStore.isConfigured(Integration.Shopify))
const hasIntegrationDescriptors = computed(() => integrationStore.integrationDescriptors.length > 0)
const isFullyConfigured = computed(() => hasShopifyConfiguration.value && hasIntegrationDescriptors.value)

const showAdvancedSettings = ref<boolean>(false)

// Connection state
const token = ref<string>("")
const storeName = ref<string>("")

const validConnection = computed(() => currentIntegrationConfig.value?.token_status === "TokenPresent")

const tokenValid = computed(() => validateToken(token.value))
const storeNameValid = computed(() => validateUrl(storeName.value))

// Here for the future
function validateToken(token: string) {
  return token !== ""
}

// Here for the future
function validateUrl(url: string) {
  return url !== ""
}

// State to show that "data cleared" after removing integration data
const dataRemoved = ref<Record<IntegrationTarget, boolean>>({
  [IntegrationTarget.ShopifySales]: false,
  [IntegrationTarget.ShopifyStock]: false,
  [IntegrationTarget.ShopifyGoodsInTransit]: false,
})

function resetDataRemoved() {
  dataRemoved.value = {
    [IntegrationTarget.ShopifySales]: false,
    [IntegrationTarget.ShopifyStock]: false,
    [IntegrationTarget.ShopifyGoodsInTransit]: false,
  }
}

onMounted(resetDataRemoved)

const noImportSettingsSelected = computed(() => !Object.values(importTargets.value).some(Boolean))

async function handleSave(tokenVal: string, url: string) {
  if (!validateToken(tokenVal) || !validateUrl(url)) {
    console.error("Invalid token or URL")
    return
  }

  await integrationStore.updateIntegration({ integration: Integration.Shopify, store_name: url, access_token: tokenVal })
  await updateTargets()
}

async function updateTargets() {
  const checkedTargets = Object.keys(importTargets.value)
    .map((key) => Number(key) as IntegrationTarget)
    .filter((key: IntegrationTarget) => importTargets.value[key])

  const targets = []
  for (const key of checkedTargets) {
    const existingDescriptor = integrationStore.integrationDescriptors.find((desc) => desc.name === key)

    if (!existingDescriptor) {
      targets.push(new IntegrationDescriptor(key))
    }
  }

  await integrationStore.updateIntegrationTargets(targets)

  const uncheckedTargets = Object.keys(importTargets.value)
    .map((key) => Number(key) as IntegrationTarget)
    .filter((key: IntegrationTarget) => !importTargets.value[key])

  // remove unchecked targets
  for (const key of uncheckedTargets) {
    await integrationStore.removeIntegrationTarget(key)
  }

  integrationStore.getIntegrationTargets()
}

function clearConnectionForm() {
  token.value = ""
  storeName.value = ""

  importTargets.value = {
    [IntegrationTarget.ShopifySales]: true,
    [IntegrationTarget.ShopifyStock]: true,
    [IntegrationTarget.ShopifyGoodsInTransit]: true,
  }

  resetDataRemoved()
}

/**
 * Computes whether the selected import targets have changed compared to the current integration descriptors.
 */
const targetsChanged = computed(() => {
  const integrationDescriptors = integrationStore.integrationDescriptors
  const importTargetsKeys = Object.keys(importTargets.value).map((key) => Number(key) as IntegrationTarget)

  for (const key of importTargetsKeys) {
    const importTargetValue = importTargets.value[key] || false
    const descriptorExists = integrationDescriptors.some((desc) => desc.name === key)

    if (importTargetValue !== descriptorExists) {
      return true
    }
  }

  return false
})

/**
 * Computed properties for button disabled states
 */
const runDisabled = computed(() => isRunning.value || !isFullyConfigured.value)
const removeDisabled = computed(() => isRunning.value || !isFullyConfigured.value)
const targetUpdateDisabled = computed(() => {
  if (targetsChanged.value === false) {
    return true
  }

  return hasShopifyConfiguration.value && noImportSettingsSelected.value
})

// Only show "Update" button when integration is already configured
const targetsOnly = computed(() => hasShopifyConfiguration.value)
const saveDisabled = computed(() => {
  if (integrationStatus.value === IntegrationRunStatus.Running) {
    return true
  }

  if (tokenValid.value === false || storeNameValid.value === false) {
    return true
  }

  if (Object.values(importTargets.value).some(Boolean) === false) {
    return true
  }

  return false
})
const saveDisabledTooltip = computed(() => {
  if (!saveDisabled.value) {
    return null
  }
  if (noImportSettingsSelected.value) {
    return "Select at least one data type to import"
  }
  if (!isFullyConfigured.value) {
    return "Enter a store URL and access token to save"
  }
  if (isFullyConfigured.value) {
    return "No changes to save"
  }
  return null
})

const runDisabledTooltip = computed(() => {
  if (!isFullyConfigured.value) {
    return "Save changes before running"
  }
  return null
})

const updateTargetTooltip = computed(() => {
  if (noImportSettingsSelected.value) {
    return "Select at least one data type to import"
  }

  return null
})

async function handleUpdateTargets() {
  if (targetUpdateDisabled.value) {
    return
  }

  await updateTargets()
}

async function handleSaveClicked() {
  if (saveDisabled.value) {
    return
  }

  const tokenIsValid = validateToken(token.value)
  const storeNameIsValid = validateUrl(storeName.value)
  if (tokenIsValid && storeNameIsValid) {
    await handleSave(token.value, storeName.value)
  }
}

function handleRunClicked() {
  if (runDisabled.value) {
    return
  }

  integrationStore.runCurrentIntegration(true)
  integrationStore.closeDialog()
}

async function handleRemoveConfigurationConfirmed() {
  if (removeDisabled.value) {
    return
  }

  await integrationStore.removeIntegration(Integration.Shopify)
  clearConnectionForm()
  await integrationStore.getIntegrationTargets()
}

async function handleRemoveDataConfirmed(targetName: IntegrationTarget) {
  if (removeDisabled.value) {
    return
  }

  try {
    await integrationStore.deleteIntegrationData(targetName)
    await integrationStore.getIntegrationTargets()
    dataRemoved.value[targetName] = true
  } catch (err) {
    console.error("Failed to remove integration data", err)
  }
}

watch(currentIntegrationConfig, (integration) => {
  if (integration !== undefined) {
    storeName.value = (integration as IntegrationConfig).store_name
  }
}, { immediate: true })

function hasDataToRemove(targetName: IntegrationTarget) {
  return `${targetName}` === `${IntegrationTarget.ShopifySales}` && integrationStore.getIntegrationByTarget(targetName)?.lastRan !== undefined
}
</script>

<template>
  <KDialog
    :open="true"
    :size="DialogSize.fullsize"
    :esc="false"
    @close="integrationStore.closeDialog"
  >
    <template #default>
      <div class="flex size-full flex-col">
        <div
          class="flex w-full flex-1 flex-col gap-4 overflow-auto"
        >
          <h2 class="font-vastago text-2xl font-bold">
            Configure Shopify integration
          </h2>

          <div
            v-if="integrationStatus === IntegrationRunStatus.Running"
            class="fixed inset-20 z-20 grid place-content-center gap-4"
          >
            <Loading class="size-40" />
            <div class="animate-pulse">
              Running integration ...
            </div>
          </div>
          <div
            v-else
            class="flex w-full flex-1 flex-col gap-4 overflow-auto"
          >
            <div class="flex flex-col gap-4 rounded-md bg-gray-100 p-4">
              <h3 class="text-lg font-bold">
                Connection details
              </h3>
              <fieldset class="max-w-50">
                <label for="store-url">Store URL</label>
                <KTextInput
                  id="store-url"
                  v-model.lazy="storeName"
                  :valid="storeNameValid && !validConnection"
                  class="allow-undo mt-2 md:min-w-96"
                  :disabled="hasShopifyConfiguration"
                />
                <p class="mt-2 mb-4 text-sm">
                  <span v-if="storeName">{{ `${storeName}.myshopify.com` }}</span>
                  <span v-else>e.g. [your-shop-name].myshopify.com</span>
                </p>
              </fieldset>

              <fieldset class="max-w-50">
                <label for="store-access-token">Storefront access token</label>
                <KTextInput
                  id="store-access-token"
                  v-model.lazy="token"
                  :valid="tokenValid && !validConnection"
                  class="allow-undo mt-2 md:min-w-96"
                  :placeholder="validConnection ? '•'.repeat(38) : ''"
                  :disabled="hasShopifyConfiguration"
                  :class="{ 'placeholder:text-gray-900': validConnection }"
                />
                <p class="mt-2 mb-4 text-sm">
                  <a
                    href="https://kaleidoscope.slite.page/p/eerl4P0Ne57oQZ/Your-Shopify-store-token"
                    class="text-violet-500 underline hover:no-underline"
                    target="_blank"
                  >
                    Get help on how to access your store token
                  </a>
                </p>
              </fieldset>
            </div>
            <div class="flex flex-col gap-4 rounded-md bg-gray-100 p-4">
              <h3 class="text-lg font-bold">
                Data to import
              </h3>

              <div
                v-for="targetName in Object.keys(importTargets).map(Number) as IntegrationTarget[]"
                :key="targetName"
                class="flex items-center gap-2"
              >
                <KCheckBox
                  :id="`target-${targetName}`"
                  :model-value="importTargets[targetName]"
                  :label="integrationTargetLabel(targetName, 'checkboxes')"
                  :disabled="hasDataToRemove(targetName)"
                  @update:model-value="importTargets[targetName] = !importTargets[targetName]"
                >
                  <span class="font-bold text-gray-900 select-none">
                    {{ integrationTargetLabel(targetName, 'checkboxes') }}
                  </span>
                </KCheckBox>

                <KConfirmButton
                  v-if="hasDataToRemove(targetName)"
                  variety="primary"
                  color="red"
                  @confirmed="handleRemoveDataConfirmed(targetName)"
                >
                  Hold to remove data
                </KConfirmButton>

                <div
                  v-if="dataRemoved[targetName]"
                  class="flex items-center gap-2 text-sm"
                >
                  <CheckRingIcon class="size-4 text-green-500" />
                  <span>
                    Data cleared
                  </span>
                </div>
              </div>
              <p
                v-if="noImportSettingsSelected"
                class="mt-4 flex items-center gap-x-2 text-sm"
              >
                <InfoIcon class="size-6 text-red-500" />
                <span>Please select at least one data type to import</span>
              </p>
            </div>

            <div
              v-if="isFullyConfigured"
              class="flex flex-col gap-4 rounded-md bg-gray-100 p-4"
            >
              <h3
                class="flex cursor-pointer items-center gap-2 text-lg font-bold select-none"
                @click="showAdvancedSettings = !showAdvancedSettings"
              >
                <ArrowDownIcon
                  class="size-4"
                  :class="{ 'rotate-180': showAdvancedSettings }"
                />
                <span>Advanced settings</span>
              </h3>
              <div
                v-show="showAdvancedSettings"
                class="animate-flip-down"
              >
                <KConfirmButton

                  variety="primary"
                  color="red"
                  :disabled="removeDisabled"
                  @confirmed="handleRemoveConfigurationConfirmed"
                >
                  Hold to remove configuration
                </KConfirmButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="pointer-events-auto inset-x-0 bottom-0 z-10 flex w-full justify-center">
          <div class="flex w-full items-center justify-end gap-4 p-2">
            <KButton
              v-tooltip.top="runDisabledTooltip"
              variety="primary"
              :class="{
                'bg-green-700! hover:bg-green-900!': !runDisabled,
              }"
              :disabled="runDisabled"
              @click="handleRunClicked"
            >
              <span v-if="integrationStatus === IntegrationRunStatus.Running">Running...</span>
              <span v-else>Run</span>
            </KButton>

            <!-- Divider -->

            <div class="h-6 w-px bg-gray-300" />

            <KButton
              v-tooltip.top="'Close integration window'"
              variety="secondary"
              color="violet"
              @click="integrationStore.closeDialog"
            >
              Close
            </KButton>

            <KButton
              v-if="targetsOnly"
              v-tooltip.top="updateTargetTooltip"
              variety="primary"
              color="violet"
              :disabled="targetUpdateDisabled"
              @click="handleUpdateTargets"
            >
              Update
            </KButton>

            <KButton
              v-else
              v-tooltip.top="saveDisabledTooltip"
              variety="primary"
              color="violet"
              :disabled="saveDisabled"
              @click="handleSaveClicked"
            >
              Save
            </KButton>
          </div>
        </div>
      </div>
    </template>
  </KDialog>
</template>
